import { Button } from '@design/components/button/Button';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { useFollowedTopics } from '@terminal/react-query-hooks/topics/useFollowedTopics';
import { useFollowTopic } from '@terminal/react-query-hooks/topics/useFollowTopic';
import { useUnfollowTopic } from '@terminal/react-query-hooks/topics/useUnfollowTopic';
import clsx from 'clsx';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './FollowThemeButton.module.scss';

export interface FollowThemeButtonProps {
  themeId: string;
  className?: string;
  variant?: 'default' | 'icon';
}

export const FollowThemeButton = ({
  themeId,
  className,
  variant = 'default',
}: FollowThemeButtonProps) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { data } = useFollowedTopics('theme');
  const { mutate: followTopic, isPending: isFollowingPending } =
    useFollowTopic();
  const { mutate: unfollowTopic, isPending: isUnfollowingPending } =
    useUnfollowTopic();

  const isFollowed = data?.values.includes(themeId) ?? false;
  const isPending = isFollowingPending || isUnfollowingPending;
  const isIcon = variant === 'icon';
  const label = isFollowed
    ? t('common:components.followThemeBtn.followingLabel')
    : t('common:components.followThemeBtn.followThemeLabel');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isFollowed) {
      unfollowTopic({ type: 'theme', code: themeId });
    } else {
      followTopic({ type: 'theme', code: themeId });
    }
  };

  const button = (
    <Button
      className={clsx(
        styles.followThemeButton,
        { [styles.active]: isFollowed, [styles.icon]: isIcon },
        className
      )}
      data-testid="follow-theme-button"
      disabled={isPending}
      iconName={isFollowed ? 'Checkmark' : 'Add'}
      label={isIcon ? undefined : label}
      onClick={handleClick}
      size={isIcon ? 'xsmall' : 'small'}
      variant="tertiary"
    />
  );

  if (isIcon) {
    return (
      <Tooltip label={label} isTouchDevice={false} inPortal>
        {button}
      </Tooltip>
    );
  }

  return button;
};
